import { useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { createAvatar } from '@dicebear/core';
import { bottts } from '@dicebear/collection';
import './layout.css';
import {
  Layout as AntLayout,
  Dropdown,
  Avatar,
  Typography,
  Space,
  Menu,
  Drawer,
  theme,
  Grid,
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  GlobalOutlined,
  SafetyOutlined,
  LeftOutlined,
  RightOutlined,
  MenuOutlined,
  SunOutlined,
  MoonOutlined,
} from '@ant-design/icons';
import { useKanagawaMode, useKanagawaToggleMode } from '@aid/theme';
import { keycloak } from '../auth/keycloak';
import { useCurrentUser } from '../auth/useCurrentUser';
import { useHasAnyPermission } from '../auth/useHasPermission';
import { useT } from '../locales';
import { useSwitchAppMenuItem } from '../app-switcher/AppSwitcherMenu';
import { buildAdminMenuItems, buildDominioMenuItems, getSelectedMenuKey } from './adminMenuItems';
import { usePermissions } from '../providers';
import { Permission } from '@aid/shared';

const { Text } = Typography;
const { Sider } = AntLayout;

function ThemeToggle() {
  const mode = useKanagawaMode();
  const toggleMode = useKanagawaToggleMode();
  const { token } = theme.useToken();
  const [hovered, setHovered] = useState(false);

  const isDark = mode === 'dark';

  return (
    <span
      role="button"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        color: hovered ? token.colorText : token.colorTextSecondary,
        background: hovered ? token.colorFillTertiary : 'transparent',
        fontSize: 16,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: 6,
        transition: 'background 160ms ease, color 160ms ease',
      }}
      onClick={toggleMode}
    >
      {isDark ? <SunOutlined /> : <MoonOutlined />}
    </span>
  );
}

function BrandMark() {
  const { token } = theme.useToken();
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: 8,
        background: token.colorPrimary,
        color: token.colorTextLightSolid,
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: '-0.02em',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        boxShadow: token.boxShadowTertiary,
      }}
    >
      <GlobalOutlined style={{ fontSize: 18 }} />
    </span>
  );
}

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCurrentUser();
  const [collapsed, setCollapsed] = useState(false);
  const [avatarHovered, setAvatarHovered] = useState(false);
  const [chevronHovered, setChevronHovered] = useState(false);
  const [menuBtnHovered, setMenuBtnHovered] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;
  const t = useT();
  const { token } = theme.useToken();
  const isAdmin = useHasAnyPermission(
    Permission.USERS_READ,
    Permission.USERS_CREATE,
    Permission.USERS_UPDATE,
    Permission.USERS_DELETE,
    Permission.ROLES_READ,
    Permission.ROLES_CREATE,
    Permission.ROLES_UPDATE,
    Permission.ROLES_DELETE,
    Permission.SOLICITUDES_READ,
    Permission.SOLICITUDES_APPROVE,
    Permission.SOLICITUDES_REJECT,
  );

  const hasDominioAccess = useHasAnyPermission(
    Permission.CLIENTES_READ,
    Permission.SERVICIOS_READ,
    Permission.COBROS_READ,
    Permission.PAGOS_READ,
    Permission.EXCEPCIONES_READ,
    Permission.VISITAS_READ,
    Permission.CATALOGOS_READ,
    Permission.PLANES_READ,
    Permission.TIPOS_SERVICIO_READ,
    Permission.COMISIONES_READ,
    Permission.COBRADOR_READ,
  );

  const { permissions } = usePermissions();
  const can = (p: Permission) => permissions.includes(p);

  const switchItem = useSwitchAppMenuItem();

  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  const userInitials = fullName
    ? fullName
        .split(/\s+/)
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : (user?.email?.charAt(0) ?? user?.username?.charAt(0) ?? '?').toUpperCase();

  // Deterministic robot avatar (DiceBear bottts) seeded by the account email:
  // same user always gets the same robot, generated locally with no external
  // service call. Initials remain as a fallback if the image ever fails.
  const avatarUri = useMemo(() => {
    const seed = user?.email ?? user?.username ?? 'guest';
    return createAvatar(bottts, {
      seed,
      size: 64,
      backgroundColor: ['#e6e8ee'],
    }).toDataUri();
  }, [user?.email, user?.username]);

  const userMenuItems = [
    {
      key: 'user-info',
      disabled: true,
      label: (
        <div style={{ padding: '4px 0', lineHeight: 1.45, minWidth: 180 }}>
          {fullName && (
            <Text strong style={{ display: 'block', fontSize: 13 }}>
              {fullName}
            </Text>
          )}
          {user?.email && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {user.email}
            </Text>
          )}
        </div>
      ),
    },
    { type: 'divider' as const },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
      onClick: () => navigate('/profile'),
    },
    switchItem,
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: () => keycloak.logout({ redirectUri: window.location.origin + '/login' }),
    },
  ];

  const adminMenuItems = buildAdminMenuItems(t);

  const sidebarItems = [
    ...(can(Permission.DASHBOARD_READ)
      ? [
          {
            key: '/',
            icon: <HomeOutlined />,
            label: t('nav.dashboard'),
          },
        ]
      : []),
    ...(hasDominioAccess ? buildDominioMenuItems(t, { can }) : []),
    ...(isAdmin
      ? [
          {
            key: 'admin',
            icon: <SafetyOutlined />,
            label: t('nav.admin'),
            children: adminMenuItems,
          },
        ]
      : []),
  ];

  const header = (
    <AntLayout.Header
      style={{
        position: isMobile ? undefined : 'sticky',
        top: isMobile ? undefined : 0,
        zIndex: isMobile ? undefined : 100,
        height: 64,
        lineHeight: 'normal',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '0 12px' : '0 28px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        backdropFilter: 'saturate(180%) blur(8px)',
      }}
    >
      <Space size={12} align="center">
        {isMobile && (
          <button
            type="button"
            aria-label="Open menu"
            title="Open menu"
            onMouseEnter={() => setMenuBtnHovered(true)}
            onMouseLeave={() => setMenuBtnHovered(false)}
            onClick={() => setDrawerOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              padding: 0,
              border: 'none',
              background: menuBtnHovered ? token.colorFillTertiary : 'transparent',
              color: menuBtnHovered ? token.colorText : token.colorTextSecondary,
              fontSize: 16,
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'background 160ms ease, color 160ms ease',
            }}
          >
            <MenuOutlined />
          </button>
        )}
        <Space size={10} align="center" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <BrandMark />
          <Text
            strong
            style={{
              color: token.colorText,
              fontSize: 17,
              letterSpacing: '-0.01em',
              lineHeight: 1,
            }}
          >
            {t('app.name')}
          </Text>
        </Space>
      </Space>

      <Space size={6} align="center">
        <ThemeToggle />
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
          <Space
            size={10}
            align="center"
            onMouseEnter={() => setAvatarHovered(true)}
            onMouseLeave={() => setAvatarHovered(false)}
            style={{
              cursor: 'pointer',
              padding: '4px',
              borderRadius: 999,
              background: avatarHovered ? token.colorFillTertiary : 'transparent',
              transition: 'background 160ms ease',
            }}
          >
            <Avatar src={avatarUri} size={32} style={{ backgroundColor: 'transparent' }}>
              {userInitials}
            </Avatar>
          </Space>
        </Dropdown>
      </Space>
    </AntLayout.Header>
  );

  const selectedKey = getSelectedMenuKey(location.pathname, sidebarItems);

  const sidebar = !isMobile ? (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      theme="light"
      width={232}
      collapsedWidth={64}
      style={{
        position: 'sticky',
        top: 64,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        background: token.colorBgContainer,
        borderRight: `1px solid ${token.colorBorderSecondary}`,
      }}
      trigger={null}
    >
      <Menu
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={location.pathname.startsWith('/facturas') ? ['facturas-group'] : []}
        items={sidebarItems}
        onClick={({ key }) => navigate(key)}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          borderRight: 0,
          padding: '12px 8px',
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '10px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <button
          type="button"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onMouseEnter={() => setChevronHovered(true)}
          onMouseLeave={() => setChevronHovered(false)}
          onClick={() => setCollapsed(!collapsed)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            padding: 0,
            border: `1px solid ${chevronHovered ? token.colorPrimary : token.colorBorderSecondary}`,
            borderRadius: '50%',
            background: token.colorBgContainer,
            color: chevronHovered ? token.colorPrimary : token.colorTextSecondary,
            fontSize: 11,
            cursor: 'pointer',
            boxShadow: token.boxShadowTertiary,
            transition: 'color 160ms ease, border-color 160ms ease',
          }}
        >
          {collapsed ? <RightOutlined /> : <LeftOutlined />}
        </button>
      </div>
    </Sider>
  ) : null;

  const drawer = isMobile ? (
    <Drawer
      placement="left"
      size={232}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      closable={false}
      styles={{ body: { padding: '12px 8px' } }}
    >
      <Menu
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={location.pathname.startsWith('/facturas') ? ['facturas-group'] : []}
        items={sidebarItems}
        onClick={({ key }) => {
          navigate(key);
          setDrawerOpen(false);
        }}
        style={{ borderRight: 0 }}
      />
    </Drawer>
  ) : null;

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {header}
      <AntLayout>
        {sidebar}
        <AntLayout.Content
          style={{
            padding: isMobile ? '16px 12px' : '28px 32px',
            background: token.colorBgLayout,
            minWidth: 0,
          }}
        >
          <Outlet />
        </AntLayout.Content>
      </AntLayout>
      {drawer}
    </AntLayout>
  );
}
